import { useMemo } from 'react';
import { useHistory } from 'react-router';

import { InvoiceFromList } from 'common/hooks/useGetInvoicesQuery';
import { MenuItem } from 'features/Work/components/EditMenu/types';
import WorkListItem from 'features/Work/components/WorkListItem';
import { useInvoiceModals } from 'features/Work/pages/Invoices/hooks/useInvoiceModals';
import * as routes from 'features/Work/routes';

import locales from './locales';

interface InvoiceListItemProps {
  invoice: InvoiceFromList;
}

const InvoiceListItem: React.FC<InvoiceListItemProps> = ({ invoice }) => {
  const history = useHistory();
  const { openModal } = useInvoiceModals();

  const menuItemsByStatus = useMemo(() => {
    const { status } = invoice;
    const invoicePaidItem: MenuItem = {
      icon: 'check',
      onClick: () => openModal({ name: 'mark-as-paid', props: invoice }),
      title: locales.markInvoiceAsPaid,
    };

    const invoiceSentItem: MenuItem = {
      icon: 'send',
      onClick: () => openModal({ name: 'mark-as-sent', props: invoice }),
      title: locales.markInvoiceAsSent,
    };

    const invoiceRevisedItem: MenuItem = {
      icon: 'edit',
      onClick: () => openModal({ name: 'revise', props: invoice }),
      title: locales.reviseInvoice,
    };

    const invoiceCanceledItem: MenuItem = {
      icon: 'cross',
      onClick: () => openModal({ name: 'cancel', props: invoice }),
      title: locales.cancelInvoice,
    };

    const invoiceEditReminderItem: MenuItem = {
      icon: 'mail',
      onClick: () => history.push(routes.getInvoice(invoice.invoiceId)),
      title: locales.editInvoiceReminder,
    };

    const editItem: MenuItem = {
      icon: 'edit',
      onClick: () => history.push(routes.getEditInvoice(invoice.invoiceId)),
      title: locales.edit,
    };

    const duplicateItem: MenuItem = {
      icon: 'copy',
      onClick: () => openModal({ name: 'duplicate', props: invoice }),
      title: locales.duplicate,
    };

    const deleteItem: MenuItem = {
      colorScheme: 'red',
      icon: 'trash',
      onClick: () => openModal({ name: 'delete', props: invoice }),
      title: locales.delete,
    };

    const resendItem: MenuItem = {
      icon: 'send',
      onClick: () =>
        history.push(`${routes.getInvoice(invoice.invoiceId)}?resend=true`),
      title: locales.resend,
    };

    const MENU_ITEMS = {
      CANCELED: [duplicateItem],
      MARKED_AS_PAID: [resendItem, duplicateItem],
      OVERDUE: [
        invoicePaidItem,
        invoiceCanceledItem,
        { ...invoiceRevisedItem, hasBottomSeparator: true },
        invoiceEditReminderItem,
        resendItem,
        duplicateItem,
      ],
      PAID: [resendItem, duplicateItem],
      PENDING: [
        invoiceSentItem,
        { ...editItem, hasBottomSeparator: true },
        duplicateItem,
        deleteItem,
      ],
      SENT: [
        invoicePaidItem,
        invoiceCanceledItem,
        { ...invoiceRevisedItem, hasBottomSeparator: true },
        invoiceEditReminderItem,
        resendItem,
        duplicateItem,
      ],
    };

    return MENU_ITEMS[status];
  }, [history, invoice, openModal]);

  return <WorkListItem item={invoice} menuItems={menuItemsByStatus} />;
};

export default InvoiceListItem;
